/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { font, weightMedium, paragraphXSmall } from 'src/components/css'

/**
 * - `success` — done / accepted / approved (green)
 * - `warning` — waiting on someone / pending (yellow)
 * - `error` — cancelled / rejected (red)
 * - `info` — a role or flag rather than a state, e.g. Admin (blue)
 * - `neutral` — anything unrecognised (grey)
 */
export type StatusTagTone = 'success' | 'warning' | 'error' | 'info' | 'neutral'

interface StatusTagProps {
  tone: StatusTagTone
  /** Shown in Title Case. */
  label: string
  /** Leading dot in the chip colour: marks a state, as opposed to a role. */
  dot?: boolean
}

/**
 * Tinted status pill: coloured label on a 16% tint of the same colour. Used
 * for invitation statuses and roles in the family list, and registration
 * statuses in Player Details.
 */
const StatusTag: React.FC<StatusTagProps> = ({ tone, label, dot = false }) => (
  <span css={[chipStyle, TONE_STYLES[tone]]}>
    {dot ? <span css={dotStyle} aria-hidden='true' /> : null}
    {capitalizeWords(label)}
  </span>
)

export default StatusTag

// ---- styled ----
const chipStyle = css`
  ${paragraphXSmall}
  ${weightMedium}
  ${font.body}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 4px;
  flex-shrink: 0;
  white-space: nowrap;
`

const TONE_STYLES: Record<StatusTagTone, ReturnType<typeof css>> = {
  success: css`
    color: ${colors.SUCCESS_300};
    background-color: rgba(16, 173, 50, 0.16);
  `,
  warning: css`
    color: ${colors.WARNING_300};
    background-color: rgba(255, 193, 7, 0.16);
  `,
  error: css`
    color: ${colors.ERROR_200};
    background-color: rgba(223, 66, 66, 0.16);
  `,
  info: css`
    color: ${colors.INFO_300};
    background-color: rgba(56, 148, 255, 0.16);
  `,
  neutral: css`
    color: ${colors.NEUTRAL_50};
    background-color: ${colors.SECONDARY_200};
  `,
}

// The dot inherits the chip text color.
const dotStyle = css`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: currentColor;
`
